import { useEffect, useRef } from 'react';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx';
import { adaptiveLanguageStore } from './adaptiveLanguageStore.tsx';
import type { GlossaryForProjectResponse } from './adaptiveLanguageState.tsx';

const GRAPHQL_ENDPOINT = '/gateway/api/graphql';

const GLOSSARY_QUERY = `
  query jsmAdaptiveLanguageGlossary($projectAri: ID!) {
    jsmAdaptiveLanguage {
      glossaryForProject(projectAri: $projectAri) {
        glossary {
          domain
          compiledGlossary
        }
      }
    }
  }
`;

/**
 * Headless global component that fetches adaptive language glossary
 * and populates the store. Renders nothing visible.
 *
 * Added as a globalComponent in serviceProjectLayout.
 */
export function AdaptiveLanguageGlobal(): null {
	const cloudId = useCloudId();
	const { data: projectContext } = useProjectContext();
	const projectId = projectContext?.projectId;
	const abortRef = useRef<AbortController | null>(null);

	useEffect(() => {
		if (!projectId || !cloudId) {
			adaptiveLanguageStore.reset();
			return;
		}

		// Abort any in-flight request from previous project navigation
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;

		const projectAri = `ari:cloud:jira:${cloudId}:project/${projectId}`;

		adaptiveLanguageStore.setState({
			domain: null,
			glossary: null,
			status: 'loading',
		});

		performPostRequest(GRAPHQL_ENDPOINT, {
			body: JSON.stringify({
				query: GLOSSARY_QUERY,
				variables: { projectAri },
			}),
			signal: controller.signal,
		})
			.then((json: { data: GlossaryForProjectResponse }) => {
				const glossaryData = json.data?.jsmAdaptiveLanguage?.glossaryForProject?.glossary;

				if (!glossaryData?.domain) {
					adaptiveLanguageStore.setState({
						domain: null,
						glossary: null,
						status: 'ready',
					});
					return;
				}

				let glossary: Record<string, string> | null = null;
				try {
					glossary = glossaryData.compiledGlossary
						? JSON.parse(glossaryData.compiledGlossary)
						: null;
				} catch {
					glossary = null;
				}

				adaptiveLanguageStore.setState({
					domain: glossaryData.domain,
					glossary,
					status: 'ready',
				});
			})
			.catch((err) => {
				if (err.name === 'AbortError') return;
				adaptiveLanguageStore.setState({
					domain: null,
					glossary: null,
					status: 'error',
				});
			});

		return () => {
			controller.abort();
		};
	}, [projectId, cloudId]);

	// Headless — renders nothing
	return null;
}
